import { useState, useRef, useEffect } from "react";
import axios from "axios";

const API = window.location.origin;

const ACTION_ICONS = {
  reset_password: "🔑",
  upgrade_plan: "⭐",
  add_topup: "➕",
  get_user: "👤",
  regenerate_notes: "🔄",
  regenerate_bulk_notes: "📚",
  update_setting: "⚙️",
  list_users: "📋",
  block_user: "🚫",
  unblock_user: "✅",
  delete_user: "🗑️",
  extend_plan: "📅",
  get_payments: "💳",
  refund_note: "💸",
  notes_stats: "📊",
  clear_all_notes: "🧹",
  list_topics: "📋",
  find_missing_notes: "🔍",
  generate_missing_notes: "⚡",
  regenerate_with_prompt: "✨",
  send_notification: "🔔",
  notification_stats: "📬",
  platform_stats: "📈",
  daily_report: "📋",
  top_users: "🏆",
  revenue_report: "💰",
  get_settings: "⚙️",
  maintenance_on: "🔧",
  maintenance_off: "✅",
  batch_job_status: "⚡",
  cancel_batch_job: "🛑",
  resume_batch_job: "▶️",
  active_note_generations: "🔄",
  vertex_ai_status: "🤖",
  send_email: "📧",
  send_email_to_user: "📧",
  send_credentials_email: "🔐",
  send_bulk_email: "📨",
  email_stats: "📊",
  create_student: "👤",
  promote_to_premium: "⭐",
};

const ACTION_LABELS = {
  reset_password: "Password Reset",
  upgrade_plan: "Plan Upgraded",
  add_topup: "Questions Added",
  get_user: "User Info",
  regenerate_notes: "Notes Cleared",
  regenerate_bulk_notes: "Bulk Notes Cleared",
  update_setting: "Setting Updated",
  list_users: "Users List",
  block_user: "User Blocked",
  unblock_user: "User Unblocked",
  delete_user: "User Deleted",
  extend_plan: "Plan Extended",
  get_payments: "Payments",
  refund_note: "Refund Noted",
  notes_stats: "Notes Stats",
  clear_all_notes: "Notes Cleared",
  list_topics: "Topics List",
  find_missing_notes: "Missing Notes",
  generate_missing_notes: "Generation Queued",
  regenerate_with_prompt: "Custom Regen",
  send_notification: "Notification Sent",
  notification_stats: "Notification Stats",
  platform_stats: "Platform Stats",
  daily_report: "Daily Report",
  top_users: "Top Users",
  revenue_report: "Revenue Report",
  get_settings: "Settings",
  maintenance_on: "Maintenance ON",
  maintenance_off: "Maintenance OFF",
  batch_job_status: "Batch Jobs",
  cancel_batch_job: "Job Cancelled",
  resume_batch_job: "Job Resumed",
  active_note_generations: "Active Generations",
  vertex_ai_status: "Vertex AI Status",
  send_email: "Email Sent",
  send_email_to_user: "Email Sent",
  send_credentials_email: "Credentials Sent",
  send_bulk_email: "Bulk Email",
  email_stats: "Email Stats",
  create_student: "Student Created",
  promote_to_premium: "Promoted to Premium",
};

function formatResult(action, result) {
  if (!result || !result.success) {
    return <span className="text-red-400">{result?.error || "Action failed"}</span>;
  }

  // User info with detailed stats
  if (action === "get_user" && result.user) {
    const u = result.user;
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Name:</span> <span className="text-white font-medium">{u.name}</span></div>
        <div><span className="text-slate-400">Mobile:</span> <span className="text-white">{u.mobile}</span></div>
        <div><span className="text-slate-400">Email:</span> <span className="text-white">{u.email || "N/A"}</span></div>
        <div><span className="text-slate-400">Plan:</span> <span className={`font-medium ${u.plan !== "free" ? "text-green-400" : "text-slate-300"}`}>{u.plan}</span></div>
        <div><span className="text-slate-400">Q-Limit:</span> <span className="text-white">{u.questions_limit === -1 ? "Unlimited" : u.questions_limit}</span></div>
        <div><span className="text-slate-400">Q-Used:</span> <span className="text-white">{u.questions_used || 0}</span></div>
        {u.is_blocked && <div className="text-red-400 font-medium">⚠️ BLOCKED</div>}
        {u.plan_expires_at && <div><span className="text-slate-400">Expires:</span> <span className="text-yellow-300">{new Date(u.plan_expires_at).toLocaleDateString("en-IN")}</span></div>}
        {u.stats && (
          <div className="border-t border-slate-600 pt-1 mt-1">
            <div><span className="text-slate-400">Descriptive:</span> <span className="text-blue-400">{u.stats.descriptive_attempts}</span></div>
            <div><span className="text-slate-400">MCQs:</span> <span className="text-blue-400">{u.stats.mcq_attempts}</span></div>
            <div><span className="text-slate-400">Notes Read:</span> <span className="text-blue-400">{u.stats.notes_read}</span></div>
          </div>
        )}
      </div>
    );
  }

  // Users list
  if (action === "list_users" && result.users) {
    return (
      <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
        {result.users.map((u, i) => (
          <div key={i} className="flex justify-between items-center py-0.5">
            <span className="text-white">{u.name} <span className="text-slate-500">({u.mobile})</span></span>
            <div className="flex items-center gap-2">
              {u.is_blocked && <span className="text-red-400 text-[10px]">blocked</span>}
              <span className={`ml-2 ${u.plan !== "free" ? "text-green-400" : "text-slate-400"}`}>{u.plan}</span>
            </div>
          </div>
        ))}
        <div className="text-slate-500 text-[10px] pt-1">Total: {result.count}</div>
      </div>
    );
  }

  // Password reset
  if (action === "reset_password") {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Name:</span> <span className="text-white">{result.name}</span></div>
        <div><span className="text-slate-400">New Password:</span> <span className="font-mono text-yellow-300 bg-slate-800 px-2 py-0.5 rounded">{result.new_password}</span></div>
      </div>
    );
  }

  // Topup added
  if (action === "add_topup") {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Name:</span> <span className="text-white">{result.name}</span></div>
        <div><span className="text-slate-400">Previous:</span> <span className="text-white">{result.previous_limit}</span> → <span className="text-green-400 font-bold">{result.new_limit}</span></div>
      </div>
    );
  }

  // Plan upgrade
  if (action === "upgrade_plan") {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Name:</span> <span className="text-white">{result.name}</span></div>
        <div><span className="text-slate-400">Plan:</span> <span className="text-green-400 font-medium">{result.plan}</span></div>
        <div><span className="text-slate-400">Expires:</span> <span className="text-yellow-300">{result.expires}</span></div>
        <div><span className="text-slate-400">Questions:</span> <span className="text-white">{result.questions_limit === -1 ? "Unlimited" : result.questions_limit}</span></div>
      </div>
    );
  }

  // Extend plan
  if (action === "extend_plan") {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Name:</span> <span className="text-white">{result.name}</span></div>
        <div><span className="text-slate-400">Extended:</span> <span className="text-green-400">+{result.extended_days} days</span></div>
        <div><span className="text-slate-400">New Expiry:</span> <span className="text-yellow-300">{result.new_expiry}</span></div>
      </div>
    );
  }

  // Platform stats
  if (action === "platform_stats" && result.stats) {
    const s = result.stats;
    return (
      <div className="text-xs space-y-1">
        <div className="grid grid-cols-2 gap-x-4">
          <div><span className="text-slate-400">Users:</span> <span className="text-white font-medium">{s.total_users}</span></div>
          <div><span className="text-slate-400">Paid:</span> <span className="text-green-400 font-medium">{s.paid_users}</span></div>
          <div><span className="text-slate-400">Free:</span> <span className="text-slate-300">{s.free_users}</span></div>
          <div><span className="text-slate-400">Conversion:</span> <span className="text-blue-400">{s.conversion_rate}</span></div>
        </div>
        <div className="border-t border-slate-600 pt-1 mt-1">
          <div><span className="text-slate-400">Evaluations:</span> <span className="text-white">{s.total_evaluations}</span></div>
          <div><span className="text-slate-400">MCQs:</span> <span className="text-white">{s.total_mcq_attempts}</span></div>
          <div><span className="text-slate-400">Cached Notes:</span> <span className="text-white">{s.total_notes_cached}</span></div>
        </div>
        {s.today && (
          <div className="border-t border-slate-600 pt-1 mt-1">
            <div className="text-slate-500">Today:</div>
            <div><span className="text-slate-400">New Users:</span> <span className="text-green-400">{s.today.new_users}</span></div>
            <div><span className="text-slate-400">Evaluations:</span> <span className="text-blue-400">{s.today.evaluations}</span></div>
          </div>
        )}
      </div>
    );
  }

  // Daily report
  if (action === "daily_report" && result.report) {
    const r = result.report;
    return (
      <div className="text-xs space-y-1">
        <div className="text-slate-300 font-medium mb-1">{result.date}</div>
        <div><span className="text-slate-400">New Users:</span> <span className="text-green-400 font-medium">{r.new_registrations}</span></div>
        <div><span className="text-slate-400">Descriptive:</span> <span className="text-blue-400">{r.descriptive_evaluations}</span></div>
        <div><span className="text-slate-400">MCQs:</span> <span className="text-blue-400">{r.mcq_attempts}</span></div>
        <div><span className="text-slate-400">Payments:</span> <span className="text-yellow-400">{r.successful_payments}</span></div>
      </div>
    );
  }

  // Top users
  if (action === "top_users" && result.top_users) {
    return (
      <div className="text-xs space-y-1">
        <div className="text-slate-400 text-[10px] mb-1">By {result.metric}</div>
        {result.top_users.map((u, i) => (
          <div key={i} className="flex justify-between">
            <span className="text-white">{i+1}. {u.name}</span>
            <span className="text-green-400 font-medium">{u.count}</span>
          </div>
        ))}
      </div>
    );
  }

  // Revenue report
  if (action === "revenue_report") {
    return (
      <div className="text-xs space-y-1">
        <div className="text-slate-400 text-[10px]">Last {result.period_days} days</div>
        <div><span className="text-slate-400">Revenue:</span> <span className="text-green-400 font-bold text-sm">{result.total_revenue}</span></div>
        <div><span className="text-slate-400">Transactions:</span> <span className="text-white">{result.total_transactions}</span></div>
        <div><span className="text-slate-400">Avg:</span> <span className="text-blue-400">{result.average_transaction}</span></div>
      </div>
    );
  }

  // Notification sent
  if (action === "send_notification") {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Sent to:</span> <span className="text-green-400 font-medium">{result.sent_count || result.message}</span></div>
        <div><span className="text-slate-400">Target:</span> <span className="text-white">{result.target}</span></div>
        {result.note && <div className="text-slate-500 text-[10px]">{result.note}</div>}
      </div>
    );
  }

  // Notification stats
  if (action === "notification_stats") {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Total Sent:</span> <span className="text-white">{result.total_notifications_sent}</span></div>
        <div><span className="text-slate-400">Users with Push:</span> <span className="text-green-400">{result.users_with_push_enabled}</span></div>
        {result.recent?.length > 0 && (
          <div className="border-t border-slate-600 pt-1 mt-1">
            <div className="text-slate-500 text-[10px] mb-1">Recent:</div>
            {result.recent.slice(0, 3).map((n, i) => (
              <div key={i} className="text-[10px] text-slate-300">{n.title} → {n.sent_to} users</div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Notes stats
  if (action === "notes_stats") {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Total Cached:</span> <span className="text-white font-medium">{result.total_notes}</span></div>
        {result.breakdown?.length > 0 && (
          <div className="max-h-32 overflow-y-auto">
            {result.breakdown.slice(0, 8).map((b, i) => (
              <div key={i} className="text-[10px] text-slate-300">
                {b.exam.toUpperCase()} • {b.subject} • {b.language}: <span className="text-blue-400">{b.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // List topics with cache status
  if (action === "list_topics" && result.topics) {
    return (
      <div className="text-xs space-y-1">
        <div className="flex justify-between mb-1">
          <span className="text-white font-medium">{result.exam} {result.subject}</span>
          <span className="text-slate-400">{result.showing}/{result.total_topics}</span>
        </div>
        <div className="max-h-40 overflow-y-auto space-y-0.5">
          {result.topics.map((t, i) => (
            <div key={i} className="flex justify-between text-[10px]">
              <span className="text-slate-300 truncate max-w-[150px]">{t.topic}</span>
              <span>
                <span className="mr-1">{t.english}</span>
                <span>{t.hindi}</span>
              </span>
            </div>
          ))}
        </div>
        <div className="text-[9px] text-slate-500 mt-1">✅ = cached, ❌ = missing</div>
      </div>
    );
  }

  // Find missing notes
  if (action === "find_missing_notes") {
    return (
      <div className="text-xs space-y-1">
        <div className="flex justify-between">
          <span className="text-white font-medium">{result.exam} {result.subject}</span>
          <span className="text-yellow-400">{result.missing_count} missing</span>
        </div>
        <div className="text-slate-400 text-[10px]">Language: {result.language}</div>
        {result.missing_topics?.length > 0 && (
          <div className="max-h-32 overflow-y-auto space-y-0.5 mt-1">
            {result.missing_topics.map((t, i) => (
              <div key={i} className="text-[10px] text-red-300">• {t}</div>
            ))}
          </div>
        )}
        {result.missing_count === 0 && (
          <div className="text-green-400 text-[10px]">All notes exist!</div>
        )}
      </div>
    );
  }

  // Generate missing notes
  if (action === "generate_missing_notes") {
    return (
      <div className="text-xs space-y-1">
        <div className="text-green-400 font-medium">{result.message}</div>
        <div><span className="text-slate-400">Exam:</span> <span className="text-white">{result.exam}</span></div>
        <div><span className="text-slate-400">Subject:</span> <span className="text-white">{result.subject}</span></div>
        <div><span className="text-slate-400">Language:</span> <span className="text-blue-400">{result.language}</span></div>
        {result.queued_topics?.length > 0 && (
          <div className="max-h-24 overflow-y-auto mt-1">
            {result.queued_topics.map((t, i) => (
              <div key={i} className="text-[10px] text-yellow-300">⏳ {t}</div>
            ))}
          </div>
        )}
        {result.custom_prompt && (
          <div className="text-[10px] text-slate-500 mt-1">Custom: {result.custom_prompt}</div>
        )}
      </div>
    );
  }

  // Regenerate with custom prompt
  if (action === "regenerate_with_prompt") {
    return (
      <div className="text-xs space-y-1">
        <div className="text-green-400">{result.message}</div>
        <div><span className="text-slate-400">Topic:</span> <span className="text-white">{result.topic}</span></div>
        <div><span className="text-slate-400">Language:</span> <span className="text-blue-400">{result.language}</span></div>
        <div className="text-[10px] text-yellow-300 mt-1">Custom: "{result.extra_instructions}"</div>
        <div className="text-[9px] text-slate-500">{result.note}</div>
      </div>
    );
  }

  // Payments list
  if (action === "get_payments" && result.payments) {
    return (
      <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
        {result.payments.map((p, i) => (
          <div key={i} className="flex justify-between py-0.5">
            <span className="text-white">₹{p.amount}</span>
            <span className={p.status === "success" ? "text-green-400" : "text-red-400"}>{p.status}</span>
          </div>
        ))}
        <div className="text-slate-500 text-[10px]">Total: {result.count}</div>
      </div>
    );
  }

  // Settings display
  if (action === "get_settings" && result.settings) {
    return (
      <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
        {Object.entries(result.settings).filter(([k]) => k !== "id" && k !== "_id").map(([key, val]) => (
          <div key={key} className="flex justify-between">
            <span className="text-slate-400">{key}:</span>
            <span className={typeof val === "boolean" ? (val ? "text-green-400" : "text-red-400") : "text-white"}>
              {typeof val === "boolean" ? (val ? "ON" : "OFF") : String(val)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // Batch job status - ENHANCED
  if (action === "batch_job_status") {
    return (
      <div className="text-xs space-y-1 max-h-48 overflow-y-auto">
        {result.summary && (
          <div className="grid grid-cols-4 gap-1 mb-2 pb-2 border-b border-slate-600">
            <div className="text-center">
              <div className="text-yellow-400 font-bold">{result.summary.processing}</div>
              <div className="text-[10px] text-slate-500">Running</div>
            </div>
            <div className="text-center">
              <div className="text-blue-400 font-bold">{result.summary.pending}</div>
              <div className="text-[10px] text-slate-500">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-green-400 font-bold">{result.summary.completed}</div>
              <div className="text-[10px] text-slate-500">Done</div>
            </div>
            <div className="text-center">
              <div className="text-red-400 font-bold">{result.summary.failed}</div>
              <div className="text-[10px] text-slate-500">Failed</div>
            </div>
          </div>
        )}
        {result.jobs?.length === 0 ? (
          <div className="text-slate-400">No batch jobs found</div>
        ) : result.jobs?.map((j, i) => (
          <div key={i} className="border-b border-slate-700 pb-1 mb-1">
            <div className="flex justify-between items-center">
              <span className="text-white text-[10px]">{j.exam?.toUpperCase()} • {j.subject}</span>
              <span className={
                j.status === "processing" ? "text-yellow-400" : 
                j.status === "completed" ? "text-green-400" : 
                j.status === "failed" ? "text-red-400" :
                j.status === "pending" ? "text-blue-400" : "text-slate-400"
              }>
                {j.status}
              </span>
            </div>
            <div className="text-[10px] text-slate-400 flex justify-between">
              <span>{j.progress} • {j.language} • {j.mode}</span>
              {j.failed > 0 && <span className="text-red-400">{j.failed} failed</span>}
            </div>
            <div className="text-[9px] text-slate-500">{j.id}</div>
          </div>
        ))}
      </div>
    );
  }

  // Active note generations
  if (action === "active_note_generations") {
    return (
      <div className="text-xs space-y-1">
        <div className="flex justify-between mb-2">
          <span className="text-slate-400">Single Notes:</span>
          <span className="text-yellow-400 font-bold">{result.active_single_notes}</span>
        </div>
        <div className="flex justify-between mb-2">
          <span className="text-slate-400">Batch Jobs:</span>
          <span className="text-blue-400 font-bold">{result.active_batch_jobs}</span>
        </div>
        {result.batch_jobs?.length > 0 && (
          <div className="border-t border-slate-600 pt-1">
            <div className="text-slate-500 text-[10px] mb-1">Active Batches:</div>
            {result.batch_jobs.map((j, i) => (
              <div key={i} className="text-[10px] text-slate-300">
                {j.exam?.toUpperCase()} {j.subject}: <span className="text-green-400">{j.progress}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Vertex AI Status
  if (action === "vertex_ai_status") {
    return (
      <div className="text-xs space-y-1">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-2 h-2 rounded-full ${result.vertex_ai?.configured ? "bg-green-400" : "bg-red-400"}`}></div>
          <span className={result.vertex_ai?.configured ? "text-green-400" : "text-red-400"}>
            {result.vertex_ai?.configured ? "Connected" : "Not Configured"}
          </span>
        </div>
        {result.vertex_ai && (
          <>
            <div><span className="text-slate-400">Model:</span> <span className="text-white">{result.vertex_ai.model}</span></div>
            <div><span className="text-slate-400">Location:</span> <span className="text-white">{result.vertex_ai.location}</span></div>
          </>
        )}
        <div className="border-t border-slate-600 pt-1 mt-1">
          <div className="text-slate-500 text-[10px] mb-1">Today's Usage:</div>
          <div><span className="text-slate-400">Notes Generated:</span> <span className="text-blue-400">{result.usage_today?.notes_generated}</span></div>
          <div><span className="text-slate-400">Evaluations:</span> <span className="text-blue-400">{result.usage_today?.evaluations}</span></div>
        </div>
        <div><span className="text-slate-400">Total Cached:</span> <span className="text-green-400">{result.total_cached_notes}</span></div>
      </div>
    );
  }

  // Email sent
  if (action === "send_email" || action === "send_email_to_user" || action === "send_credentials_email") {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">To:</span> <span className="text-white">{result.to}</span></div>
        {result.name && <div><span className="text-slate-400">Name:</span> <span className="text-white">{result.name}</span></div>}
        {result.subject && <div><span className="text-slate-400">Subject:</span> <span className="text-slate-300">{result.subject}</span></div>}
        <div className="text-green-400 text-[10px]">{result.message}</div>
      </div>
    );
  }

  // Bulk email sent
  if (action === "send_bulk_email") {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Target:</span> <span className="text-white">{result.target}</span></div>
        <div><span className="text-slate-400">Sent:</span> <span className="text-green-400 font-medium">{result.sent_count}</span></div>
        {result.failed_count > 0 && <div><span className="text-slate-400">Failed:</span> <span className="text-red-400">{result.failed_count}</span></div>}
      </div>
    );
  }

  // Email stats
  if (action === "email_stats") {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Total Sent:</span> <span className="text-white font-medium">{result.total_emails_sent}</span></div>
        <div><span className="text-slate-400">Users with Email:</span> <span className="text-green-400">{result.users_with_email}</span></div>
        {result.breakdown && (
          <div className="border-t border-slate-600 pt-1 mt-1">
            <div><span className="text-slate-400">Credentials:</span> <span className="text-blue-400">{result.breakdown.credentials}</span></div>
            <div><span className="text-slate-400">Bulk:</span> <span className="text-blue-400">{result.breakdown.bulk}</span></div>
            <div><span className="text-slate-400">Custom:</span> <span className="text-blue-400">{result.breakdown.custom}</span></div>
          </div>
        )}
        {result.recent?.length > 0 && (
          <div className="border-t border-slate-600 pt-1 mt-1">
            <div className="text-slate-500 text-[10px] mb-1">Recent:</div>
            {result.recent.map((e, i) => (
              <div key={i} className="text-[10px] text-slate-300">{e.to} • {e.subject}</div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Create student
  if (action === "create_student" && result.mobile) {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Name:</span> <span className="text-white font-medium">{result.name}</span></div>
        <div><span className="text-slate-400">Mobile:</span> <span className="font-mono text-yellow-300 bg-slate-800 px-2 py-0.5 rounded">{result.mobile}</span></div>
        <div><span className="text-slate-400">Email:</span> <span className="text-white">{result.email}</span></div>
        <div><span className="text-slate-400">Password:</span> <span className="font-mono text-yellow-300 bg-slate-800 px-2 py-0.5 rounded">{result.password}</span></div>
        <div className="mt-1 text-green-400 text-[10px]">Account created — share credentials with student</div>
      </div>
    );
  }

  // Promote to premium
  if (action === "promote_to_premium" && result.mobile) {
    return (
      <div className="text-xs space-y-1">
        <div><span className="text-slate-400">Name:</span> <span className="text-white font-medium">{result.name}</span></div>
        <div><span className="text-slate-400">Mobile:</span> <span className="text-white">{result.mobile}</span></div>
        <div><span className="text-slate-400">Plan:</span> <span className="text-green-400 font-bold">⭐ Premium</span></div>
        <div><span className="text-slate-400">Duration:</span> <span className="text-white">{result.days} days</span></div>
        <div><span className="text-slate-400">Expires:</span> <span className="text-yellow-300">{result.expires}</span></div>
        <div><span className="text-slate-400">Questions:</span> <span className="text-white">{result.questions_limit}</span></div>
        {result.email_sent && <div className="text-green-400 text-[10px]">Credentials email sent!</div>}
        {result.email_error && <div className="text-orange-400 text-[10px]">Email: {result.email_error}</div>}
      </div>
    );
  }

  // Default success message
  return <span className="text-green-400 text-xs">{result.message || "Done!"}</span>;
}

function BotMessage({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center mr-2 flex-shrink-0 mt-1 text-xs font-bold text-white">AI</div>
      )}
      <div className={`max-w-[85%] ${isUser ? "items-end" : "items-start"} flex flex-col gap-1`}>
        {/* Message bubble */}
        <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
          isUser 
            ? "bg-violet-600 text-white rounded-tr-sm" 
            : "bg-slate-700/80 text-slate-100 rounded-tl-sm"
        }`}>
          {msg.text.split("**").map((part, i) =>
            i % 2 === 1
              ? <strong key={i} className="font-semibold">{part}</strong>
              : part.includes("`")
                ? part.split("`").map((p, j) =>
                    j % 2 === 1
                      ? <code key={j} className="bg-slate-800 px-1.5 py-0.5 rounded text-yellow-300 font-mono text-xs">{p}</code>
                      : p
                  )
                : part
          )}
        </div>

        {/* Action result card */}
        {msg.action && msg.actionResult && (
          <div className="bg-slate-800/90 border border-slate-600/50 rounded-xl px-3 py-2 w-full">
            <div className="flex items-center gap-1.5 mb-2">
              <span>{ACTION_ICONS[msg.action] || "✅"}</span>
              <span className={`text-xs font-semibold ${msg.actionResult.success ? "text-green-400" : "text-red-400"}`}>
                {msg.actionResult.success ? ACTION_LABELS[msg.action] || "Done" : "Failed"}
              </span>
            </div>
            {formatResult(msg.action, msg.actionResult)}
          </div>
        )}
      </div>
    </div>
  );
}

const QUICK_COMMANDS = [
  { label: "📊 Stats", prompt: "platform stats dikhao" },
  { label: "⚡ Jobs", prompt: "batch jobs dikhao" },
  { label: "👤 New Student", prompt: "create student name: " },
  { label: "⭐ Premium", prompt: "promote to premium mobile: " },
  { label: "🔑 Reset Pwd", prompt: "Reset password for mobile: " },
  { label: "➕ +50 Q", prompt: "Add 50 questions to mobile: " },
  { label: "👤 User Info", prompt: "Get info for mobile: " },
  { label: "🔍 Missing", prompt: "find missing hindi notes for tgt science" },
];

const COMMAND_CATEGORIES = [
  { 
    label: "👥 Users",
    commands: ["get user info", "list paid users", "block user", "unblock user", "search user"]
  },
  { 
    label: "🆕 Create & Promote",
    commands: ["create student Rahul 9876543210", "create student with email rahul@gmail.com", "promote 9876543210 to premium", "promote to premium 6 months"]
  },
  { 
    label: "💳 Plans",
    commands: ["upgrade to premium", "add 50 questions", "extend plan 30 days"]
  },
  { 
    label: "📚 Notes",
    commands: ["regenerate tgt science notes", "notes stats", "clear all english notes"]
  },
  { 
    label: "📊 Analytics",
    commands: ["platform stats", "daily report", "top users by evaluations", "revenue report"]
  }
];

export default function AdminBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Namaste! Main **ADMIN BOT** hun - aapka SUPERCHARGED assistant! 🚀\n\n**Mere Powers:**\n• 🆕 **Create Student** - naya account banao (`create student Rahul 9876543210`)\n• ⭐ **Promote Premium** - premium do (`promote 9876543210 to premium`)\n• 👥 **User Management** - password reset, block/unblock, delete\n• 💳 **Plans** - upgrade, topup, extend expiry\n• 📚 **Notes** - regenerate, clear cache, stats\n• 🔔 **Notifications** - send to all/paid/free users\n• 📧 **Email** - credentials, custom, bulk emails via Resend\n• 📊 **Analytics** - platform stats, daily report, revenue\n• ⚙️ **Settings** - maintenance mode, limits\n\nTry: *\"create student Rahul 9876543210\"* ya *\"9876543210 ko premium karo\"*" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `admin_${Date.now()}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      inputRef.current?.focus();
    }
  }, [messages, open]);

  const sendMessage = async (text = input.trim()) => {
    if (!text || loading) return;
    setInput("");

    const userMsg = { role: "user", text };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/api/admin/bot/chat`, {
        message: text,
        session_id: sessionId
      });

      const data = res.data;
      const botMsg = {
        role: "bot",
        text: data.message || "Done!",
        action: data.action,
        actionResult: data.action_result
      };
      setMessages(prev => [...prev, botMsg]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: "bot",
        text: "Sorry, kuch error aa gaya. Please try again.",
        action: null,
        actionResult: null
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95"
        style={{
          background: open
            ? "linear-gradient(135deg, #7c3aed, #4f46e5)"
            : "linear-gradient(135deg, #6d28d9, #3730a3)",
          boxShadow: "0 8px 32px rgba(109, 40, 217, 0.5)"
        }}
        data-testid="admin-bot-btn"
        title="Admin AI Bot"
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
            <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2zM7 14a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm5 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm5 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
          </svg>
        )}
      </button>

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 flex flex-col rounded-2xl overflow-hidden shadow-2xl"
          style={{
            width: "380px",
            height: "560px",
            background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)",
            border: "1px solid rgba(139, 92, 246, 0.3)",
            boxShadow: "0 24px 64px rgba(109, 40, 217, 0.25), 0 0 0 1px rgba(139, 92, 246, 0.15)"
          }}
          data-testid="admin-bot-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center gap-3 flex-shrink-0" style={{ background: "linear-gradient(90deg, #4c1d95, #1e1b4b)", borderBottom: "1px solid rgba(139,92,246,0.2)" }}>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-400 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
              </svg>
            </div>
            <div>
              <div className="text-white font-bold text-sm">Admin Bot</div>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
                <span className="text-green-400 text-xs">Gemini 2.0 Flash • Supercharged</span>
              </div>
            </div>
            <button
              onClick={() => setMessages([{ role: "bot", text: "Conversation cleared! Fresh start kar lete hain 😊" }])}
              className="ml-auto text-slate-400 hover:text-white text-xs px-2 py-1 rounded hover:bg-slate-700/50 transition-colors"
              title="Clear chat"
            >
              Clear
            </button>
          </div>

          {/* Quick Commands */}
          <div className="px-3 py-2 flex gap-2 overflow-x-auto flex-shrink-0 scrollbar-hide" style={{ borderBottom: "1px solid rgba(139,92,246,0.1)" }}>
            {QUICK_COMMANDS.map((cmd) => (
              <button
                key={cmd.label}
                onClick={() => setInput(cmd.prompt)}
                className="text-xs px-2.5 py-1.5 rounded-lg bg-slate-700/60 text-slate-300 hover:bg-violet-600/50 hover:text-white whitespace-nowrap transition-all flex-shrink-0 border border-slate-600/30 hover:border-violet-500/50"
              >
                {cmd.label}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1" style={{ scrollbarWidth: "thin", scrollbarColor: "#334155 transparent" }}>
            {messages.map((msg, i) => (
              <BotMessage key={i} msg={msg} />
            ))}
            {loading && (
              <div className="flex justify-start mb-3">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center mr-2 mt-1 text-xs font-bold text-white flex-shrink-0">AI</div>
                <div className="bg-slate-700/80 rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex gap-1.5 items-center h-4">
                    <div className="w-2 h-2 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="w-2 h-2 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="w-2 h-2 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "300ms" }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 flex-shrink-0" style={{ borderTop: "1px solid rgba(139,92,246,0.15)" }}>
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Type a command... (e.g. 'reset password 9876543210')"
                className="flex-1 bg-slate-700/50 text-slate-100 text-sm rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-violet-500 placeholder-slate-500 border border-slate-600/30"
                rows={2}
                style={{ minHeight: "44px", maxHeight: "88px" }}
                data-testid="admin-bot-input"
              />
              <button
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(135deg, #7c3aed, #4f46e5)" }}
                data-testid="admin-bot-send"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
